// The anchors of a deck (AB#350): every named anchor on a whole element (`ElementSnapshot.anchor`)
// or on letters (`Run.anchor`, in a text box, a shape, a connector label or a table cell), slide by
// slide in deck order — what the artefact's anchor list (lib/artefact-anchors.ts) shows for a slides
// part, and what a followed anchor link is resolved against. Read-only: nothing is claimed.
import type * as Y from 'yjs'
import { slideIds, snapshotSlide, type Anchor, type Block, type ElementSnapshot, type SlideSnapshot } from './model'

/** Where an anchor sits in a deck. */
export interface DeckAnchor extends Anchor {
  slideId: string
  /** 0-based position of the slide in the deck. */
  slideIndex: number
  /** The element holding it (the anchored element, or the one whose text holds the letters). */
  elementId: string
  /** 'element': the whole element; 'text': letters of its text. */
  on: 'element' | 'text'
  /** The anchored element's kind (`on: 'element'`). */
  elementKind?: ElementSnapshot['kind']
  /** The anchored letters (`on: 'text'`), every run of the anchor in the element joined. */
  text?: string
}

/** Every anchored run of some blocks, in order: anchor id → its letters, joined. */
function runAnchors(blocks: readonly Block[], out: Map<string, { anchor: Anchor; text: string }>): void {
  for (const b of blocks) {
    if (b.type === 'p') {
      for (const inl of b.inlines) {
        if ('br' in inl || !inl.anchor) continue
        const seen = out.get(inl.anchor.id)
        if (seen) seen.text += inl.text
        else out.set(inl.anchor.id, { anchor: inl.anchor, text: inl.text })
      }
    } else for (const item of b.items) runAnchors(item, out)
  }
}

/** The anchors of one slide, in element order: an element's own anchor, then those of its letters. */
export function slideAnchors(s: SlideSnapshot, slideIndex: number): DeckAnchor[] {
  const out: DeckAnchor[] = []
  const walk = (ids: readonly string[]): void => {
    for (const id of ids) {
      const e = s.elements[id]
      if (!e) continue
      if (e.anchor) out.push({ ...e.anchor, slideId: s.id, slideIndex, elementId: id, on: 'element', elementKind: e.kind })
      const runs = new Map<string, { anchor: Anchor; text: string }>()
      runAnchors(e.text ?? [], runs)
      for (const row of e.cells ?? []) for (const cell of row) runAnchors(cell, runs)
      for (const l of e.labels ?? []) runAnchors(l.text ?? [], runs)
      for (const { anchor, text } of runs.values()) out.push({ ...anchor, slideId: s.id, slideIndex, elementId: id, on: 'text', text })
      if (e.kind === 'group') walk(e.children ?? [])
    }
  }
  walk(s.order)
  return out
}

/** Every anchor of a deck, slide by slide in deck order. */
export function deckAnchors(doc: Y.Doc): DeckAnchor[] {
  return slideIds(doc).flatMap((id, i) => slideAnchors(snapshotSlide(doc, id), i))
}
